// © Microsoft Corporation. All rights reserved.

import { timeToFunctional, timeToVideoStart, timeToVisuallyReady } from "./PerfMarkerConstants";

import { perfMarker } from "./PerfMarker";

/** Collection of marker names grouped by marker type */
export interface MarkerNames {
    /** Array of timeToFunctional names */
    TTF?: string[];

    /** Array of timeToVisualReady names */
    TTVR?: string[];
}

let remainingMarkers: Set<string>;
let remainingTtvrMarkers: Set<string>;

let ttvrPromise: Promise<[string, number][]>;
let resolveTtvrCallback: (markers: [string, number][]) => void;

let perfMarkerPromise: Promise<[string, number][]>;
let perfMarkerResolveCallback: (markers: [string, number][]) => void;

let ttvsMarkerPromise: Promise<[string, number][]>;
let resolveTtvsCallback: (ttvs: [string, number][]) => void;

/** The markers map. Key is the experience name, e.g. "River". Value is the number of milliseconds it took to reach that marker. */
let markers: Map<string, number> = new Map();

const ttfPrefix: string = "TTF.";
const ttvrPrefix: string = "TTVR.";
const ttvsPrefix: string = "TTVS.";

let isSetMarkersCalled: boolean = false;

/**
 * Saves the Time To Functional timestamp for the given marker and also marks the performance timeline.
 * If the same marker is added again, it overrides with the original marker.
 * @param name - The marker name
 * @param markTimeNA - To set -1 mark time for the TTF to resolve the dependency.
 * @param time - The elapsed time since window.performance.timing.navigationStart
 */
export function markTtf(name: string = "", markTimeNA: boolean = false, time: number = window.performance.now()): void {
    return setMark(ttfPrefix, name, markTimeNA, time);
}

/**
 * Saves the Time To Visually Ready timestamp for the given marker and also marks the performance timeline.
 * If the same marker is added again, it overrides with the original marker.
 * @param name - The marker name
 * @param markTimeNA - To set -1 mark time for the TTVR to resolve the dependency.
 * @param time - The elapsed time since window.performance.timing.navigationStart
 */
export function markTtvr(name: string = "", markTimeNA: boolean = false, time: number = window.performance.now()): void {
    return setMark(ttvrPrefix, name, markTimeNA, time);
}

/**
 * Helper that wraps the markTtvr call inside a request animation frame and setTimeout
 * with the idea that this will allow the experience time to render before setting the mark
 * @param name The marker name
 * @param markTimeNA To set -1 mark time for the TTVR to resolve the dependency
 */
export function markTtvrRaf(name: string = "", markTimeNA: boolean = false): void {
    window.requestAnimationFrame(() => {
        setTimeout(() => {
            markTtvr(name, markTimeNA);
        }, 0);
    });
}

/**
 * Saves the Time to Video Start timestamp for the given marker and also marks the performance timeline.
 * If the same marker is added again, it overrides with the original marker.
 * @param name - The marker name
 * @param markTimeNA - To set -1 mark time for the TTVR to resolve the dependency.
 * @param time - The elapsed time since window.performance.timing.navigationStart
 */
export function markTtvs(name: string = "", markTimeNA: boolean = false, time: number = window.performance.now()): void {
    return setMark(ttvsPrefix, name, markTimeNA, time);
}

/**
 * Saves the timestamp for the given marker and also marks the performance timeline.
 * If the same marker is added again, it overrides with the original marker.
 * @param name - The marker name
 * @param markTimeNA - To set -1 mark time for the ttvr to resolve the dependency.
 * @param time - The elapsed time since window.performance.timing.navigationStart
 */
function setMark(prefix: string, name: string, markTimeNA: boolean, time: number): void {
    if (!(name && name.length)) {
        return;
    }

    if (!(prefix && prefix.length)) {
        return;
    }

    const markerFullName = prefix + name;

    // To check if the marker exists then return.
    if (markers.get(markerFullName)) {
        return;
    }

    let existingMarker = performance.getEntriesByName && performance.getEntriesByName(markerFullName);
    let isMarkerSet = Boolean(existingMarker && existingMarker.length);

    if (!isMarkerSet) {
        perfMarker.singleMark(markerFullName);

        if (markTimeNA) {
            markers.set(markerFullName, -1);
        } else {
            markers.set(markerFullName, Math.round(time));
        }
    } else {
        markers.set(markerFullName, Math.round(existingMarker[0].startTime));
    }

    // Notify any callers waiting for TTVR reports
    if (remainingTtvrMarkers && remainingTtvrMarkers.delete(markerFullName) && remainingTtvrMarkers.size === 0 && resolveTtvrCallback) {
        resolveTtvrCallback(getTtvrMarkers());
    }

    // Notify any callers waiting for reports
    if (remainingMarkers && remainingMarkers.delete(markerFullName) && remainingMarkers.size === 0 && perfMarkerResolveCallback) {
        perfMarkerResolveCallback(getMarkers());
    }

    // Resolve TTVS if this is ttvs. TTVS only need one time to mark instead of multiple markers.
    if (prefix === ttvsPrefix && resolveTtvsCallback) {
        resolveTtvsCallback(getTtvsMarkers());
    }
}

/**
 * Sets the collection of primary perf markers to use for waitForAllPerfMarkers, waitForTtvr .
 * @param markerNames The collection of primary TTF and TTVR perf markers.
 */
export function setMarkers(markerNames: MarkerNames): void {
    remainingMarkers = getRemainingMarkers(markerNames);
    remainingTtvrMarkers = getRemainingMarkers(markerNames, "TTVR");

    if (resolveTtvrCallback && remainingTtvrMarkers.size === 0) {
        perfMarkerResolveCallback(getTtvrMarkers());
    }

    if (perfMarkerResolveCallback && remainingMarkers.size === 0) {
        perfMarkerResolveCallback(getMarkers());
    }

    isSetMarkersCalled = true;
}

/**
 * Determines whether setMarkers has been called.
 */
export function setMarkersCalled(): boolean {
    return isSetMarkersCalled;
}

/**
 * Waits for the all perf markers supplied to setMarkers() to be logged.
 * @returns A promise with all of the TTF and TTVR perf markers, including the overall page marker
 */
export function waitForAllPerfMarkers(): Promise<[string, number][]> {
    return waitForPerfMarkers();
}

/**
 * Waits for the TTVR markers supplied to setMarkers() to be logged.
 * @returns A promise with the TTVR perf markers, including the overall page TTVR marker
 */
export function waitForTtvr(): Promise<[string, number][]> {
    // If we have a promise, return it. It may already be completed.
    if (ttvrPromise) {
        return ttvrPromise;
    }

    if (remainingTtvrMarkers && remainingTtvrMarkers.size === 0) {
        return Promise.resolve(getTtvrMarkers());
    }

    // If the markers are not yet declared or are incomplete, set up a promise to resolve when they are all done.
    ttvrPromise = new Promise<[string, number][]>(resolve => {
        resolveTtvrCallback = resolve;
    });

    return ttvrPromise;
}

/**
 * Waits for the perf markers supplied to setMarkers() to be logged.
 * @returns A promise with all of the TTF and TTVR perf markers, including the overall page marker
 */
function waitForPerfMarkers(): Promise<[string, number][]> {
    // If we have a promise, return it. It may already be completed.
    if (perfMarkerPromise) {
        return perfMarkerPromise;
    }

    if (remainingMarkers && remainingMarkers.size === 0) {
        return Promise.resolve(getMarkers());
    }

    // If the markers are not yet declared or are incomplete, set up a promise to resolve when they are all done.
    perfMarkerPromise = new Promise<[string, number][]>(resolve => {
        perfMarkerResolveCallback = resolve;
    });

    return perfMarkerPromise;
}

/**
 * Wait for the ttvs marker to be logged
 * @returns A promise with TTVS perf time in ms
 */
export function waitForTtvsMarker(): Promise<[string, number][]> {
    if (ttvsMarkerPromise) {
        return ttvsMarkerPromise;
    }

    ttvsMarkerPromise = new Promise<[string, number][]>(resolve => {
        resolveTtvsCallback = resolve;
    });

    return ttvsMarkerPromise;
}

/**
 * Returns TTVR of given entry if exists in markers array
 * @param entryName the entry name for the marker eg. "TTVR.xx" or just "TTVR"
 * @returns the TTVR value for that entry name OR undefined if entry does not exist
 */
export function getTtvrOfEntry(entryName: string): number {
    const ttvrMarkers = getTtvrMarkers();
    const marker = ttvrMarkers.find((mark) => mark[0] === entryName) || [];

    return marker[1];
}

/**
 * Returns array of TTF markers, including the page-level marker. For example:
 * [["TTF.Search", 100], ["TTF", 100]]
 * @returns The array of markers
 */
function getTtfMarkers(): [string, number][] {
    return getMarkersWithType(ttfPrefix);
}

/**
 * Returns array of TTVR markers, including the page-level marker. For example:
 * [["TTVR.Search", 100], ["TTVR.TopSites", 100.123], ["TTVR.River", 100.504], ["TTVR", 100.504]]
 * @returns The array of markers
 */
function getTtvrMarkers(): [string, number][] {
    return getMarkersWithType(ttvrPrefix, timeToVisuallyReady);
}

/**
 * Returns array of TTVS markers, including the page-level marker. For example:
 * [["TTVS.VideoContent", 1200], ["TTVS", 1200]]
 * @returns The array of markers
 */
function getTtvsMarkers(): [string, number][] {
    return getMarkersWithType(ttvsPrefix, timeToVideoStart);
}

/**
 * Returns array of a provided markers baesd on markerName, optionally including the page-level marker.
 * @param markerPrefix the marker prefix for the markers
 * @param markerName the optional page-level markerName
 * @returns The array of markers
 */
function getMarkersWithType(markerPrefix: string, markerName?: string) {
    const result: [string, number][] = [];
    let maxValue = 0;
    markers.forEach((value: number, key: string) => {
        if (key.startsWith(markerPrefix)) {
            result.push([key, value]);
            if (value > maxValue) {
                maxValue = value;
            }
        }
    });

    // Add the max, overall marker at the end
    if(markerName) {
        result.push([markerName, maxValue]);
    }

    return result;
}

/**
 * Returns array of markers, including the page-level marker. For example:
 * Note: didn't add TTVS in getMarkers because TTVS only applies to specific page type and special cases.
 * It could be fired way after in the page cycle.
 * [["TTVR.Search", 100], ["TTVR.TopSites", 100.123], ["TTVR.River", 100.504], ["TTVR", 100.504]]
 * @returns The array of markers
 */
function getMarkers(): [string, number][] {
    const ttvrResults = getTtvrMarkers();
    const ttfResults = getTtfMarkers();
    const result: [string, number][] = [...ttvrResults, ...ttfResults];

    return result;
}

/**
 * Returns markers yet to be set
 * @param markerNames The names of the markers to check. e.g. { TTVR: ["River", "TopSites"], TTF: ["SearchBox"] }
 * @param markerType The type of markers to return "TTVR", "TTF", or if undefined all
 * @returns The set of markers that have not yet been set.
 */
function getRemainingMarkers(markerNames: MarkerNames, markerType?: string): Set<string> {
    let result: Set<string> = new Set<string>();
    for (let markerName of flattenMarkerNames(markerNames, markerType)) {
        if (!markers.get(markerName)) {
            result.add(markerName);
        }
    }

    return result;
}

/**
 * Put marker names into a expanded/flattened array [ "TTVR.River", "TTVR.TopSites", "TTF.SearchBox" ]
 * @param markerNames The names of the markers to check. e.g. { TTVR: [ "River", "TopSites" ], TTF: [ "SearchBox" ] }
 * @param markerType The type of markers to return "TTVR", "TTF", or if undefined all
 */
function flattenMarkerNames(markerNames: MarkerNames, markerType: string): string[] {
    let ttvr: string[] = [];
    let ttf: string[] = [];

    if (markerNames && markerNames.TTVR && (!markerType || markerType === "TTVR")) {
        ttvr = markerNames.TTVR.map((n) => { return ttvrPrefix + n; });
    }

    if (markerNames && markerNames.TTF && (!markerType || markerType === "TTF")) {
        ttf = markerNames.TTF.map((n) => { return ttfPrefix + n; });
    }

    return [...ttvr, ...ttf];
}
